import { PayloadAction } from '@reduxjs/toolkit';
import { call, put } from 'redux-saga/effects';
import { SagaIterator } from '@redux-saga/types';
import { toast } from 'react-toastify';
import i18n from '@/i18n';

import { amlbotAPI } from '@/api';
import type {
  GetTwoFACodeRequest,
  GetTwoFACodeResponse,
  UpdateTwoFAUsageRequest,
  UpdateTwoFAUsageResponse,
} from '@/api';
import { COMMON_CONTAINER_ID, ErrorCodeEnum } from '@/config';
import { parseError } from '@/redux/helpers';
import {
  twoFAActions,
  twoFAActivateModalActions,
  twoFADeActivateModalActions,
  userActions,
} from '@/redux/actions';
import { twoFAMappers } from '@/redux/mappers';

function* getTwoFACode(
  action: PayloadAction<GetTwoFACodeRequest>,
): SagaIterator {
  try {
    const response: GetTwoFACodeResponse = yield call(amlbotAPI.getTwoFaCode);
    const data = twoFAMappers.getTwoFaCode(response);
    yield put(twoFAActions.setData(data));
  } catch (error: unknown) {
    const { message } = parseError(error);
    yield put(twoFAActions.setError(message));
  } finally {
    yield put(twoFAActions.setLoading(false));
  }
}

function* enableTwoFA(
  action: PayloadAction<UpdateTwoFAUsageRequest>,
): SagaIterator {
  try {
    const { payload } = action;
    const response: UpdateTwoFAUsageResponse = yield call(
      amlbotAPI.updateTwoFaUsage,
      payload,
    );
    const data = { use2Fa: response.data.is2faEnabled };
    toast.success(i18n.t('translation.2fa_activated_msg'), {
      containerId: COMMON_CONTAINER_ID,
    });

    yield put(twoFAActivateModalActions.closeModal());
    yield put(userActions.updateAttribute(data));
  } catch (error: unknown) {
    const { message, errorCode } = parseError(error);

    // Optionally handle errorCode - ErrorCodeEnum.FIELD_REQUIRED
    if (errorCode === ErrorCodeEnum.TWO_FA_ERROR) {
      yield put(twoFAActions.setError(i18n.t('translation.2fa_token_error')));
    } else {
      yield put(twoFAActions.setError(message));
    }
  } finally {
    yield put(twoFAActions.setLoading(false));
  }
}

function* disableTwoFA(
  action: PayloadAction<UpdateTwoFAUsageRequest>,
): SagaIterator {
  try {
    const { payload } = action;
    const response: UpdateTwoFAUsageResponse = yield call(
      amlbotAPI.updateTwoFaUsage,
      payload,
    );
    const data = { use2Fa: response.data.is2faEnabled };
    toast.success(i18n.t('translation.2fa_turned_off_msg'), {
      containerId: COMMON_CONTAINER_ID,
    });
    yield put(twoFADeActivateModalActions.closeModal());
    yield put(userActions.updateAttribute(data));
  } catch (error: unknown) {
    const { message, errorCode } = parseError(error);

    // Optionally handle errorCode - ErrorCodeEnum.FIELD_REQUIRED
    if (errorCode === ErrorCodeEnum.TWO_FA_ERROR) {
      yield put(twoFAActions.setError(i18n.t('translation.2fa_token_error')));
    } else {
      yield put(twoFAActions.setError(message));
    }
  } finally {
    yield put(twoFAActions.setLoading(false));
  }
}

export { getTwoFACode, enableTwoFA, disableTwoFA };
